import _ from 'underscore'
import React from 'react'
import PropTypes from 'prop-types'

import Form from '../elements/Form'
import TextInput from '../elements/forms/TextInput'
import StaticInput from '../elements/forms/StaticInput'
import DateInput from '../elements/forms/DateInput'
import LocationSelect from '../elements/forms/LocationSelect'
import LocationStickers from '../elements/LocationStickers'
import Dismissible from '../elements/Dismissible'
import AppUtils from '../../utils/AppUtils'

const ERROR = {
  EMPTY_DATE: 'La date ne peut pas être vide.',
  EMPTY_LOCATION: 'Veuillez sélectionner un emplacement',
  EMPTY_TITLE: 'Le titre ne peut pas être vide.'
}

class InventoryForm extends React.Component {

  constructor(props) {

    // Properties
    super(props)

    // State
    this.state = {
      input: this.getInitialInput(props),
      formErrors: []
    }

    // Bind events
    this.onChangeInputDate = this.onChangeInputDate.bind(this)
    this.onChangeInputLocation = this.onChangeInputLocation.bind(this)
    this.onChangeInputTitle = this.onChangeInputTitle.bind(this)
    this.onChangeInputPeople = this.onChangeInputPeople.bind(this)
    this.onChangeInputRemark = this.onChangeInputRemark.bind(this)
    this.onSubmit = this.onSubmit.bind(this)
    this.onPrint = this.onPrint.bind(this)

    // Register submit event
    if (_.isFunction(this.props.registerSubmit)) {
      this.props.registerSubmit(this.onSubmit)
    }

    // Register print event
    if (_.isFunction(this.props.registerPrint)) {
      this.props.registerPrint(this.onPrint)
    }
  }

  getInitialInput(props = this.props) {
    return {
      date: (props.data && props.data.date) || null,
      location: (props.data && props.data.location && props.data.location.id) || props.location || null,
      title: (props.data && props.data.title) || '',
      people: (props.data && props.data.people) || '',
      remark: (props.data && props.data.remark) || ''
    }
  }

  setInput(input = {}) {
    this.setState((prevState, props) => ({
      input: _.extend(
        {},
        prevState.input,
        input
      )
    }))
  }

  componentDidUpdate(prevProps, prevState) {
    // When async fetch finished
    if (prevProps.isLoading && !this.props.isLoading) {
      this.setState({
        input: this.getInitialInput()
      })
    }
  }

  onChangeInputDate(date) {
    this.setInput({ date })
  }

  onChangeInputLocation(location) {
    this.setInput({ location })
  }

  onChangeInputTitle(title) {
    this.setInput({ title })
  }

  onChangeInputPeople(people) {
    this.setInput({ people })
  }

  onChangeInputRemark(remark) {
    this.setInput({ remark })
  }

  onSubmit() {
    // Check that fields have been filled
    const formErrors = []
    if (!this.props.data) {
      if (_.isNull(this.state.input.date)) {
        formErrors.push(ERROR.EMPTY_DATE)
      }
      if (_.isNull(this.state.input.location)) {
        formErrors.push(ERROR.EMPTY_LOCATION)
      }
    }
    if (!_.isString(this.state.input.title) || this.state.input.title.length === 0) {
      formErrors.push(ERROR.EMPTY_TITLE)
    }
    this.setState({
      formErrors
    })
    if (!_.isEmpty(formErrors)) {
      return;
    }

    // Build and submit correctly formatted input
    this.props.onSubmit(this.buildFinalInput())
  }

  onPrint() {
    // No print option available if data is set (edition mode)
    if (this.props.data) { return; }

    // Check that fields have been filled
    const formErrors = []
    if (_.isNull(this.state.input.location)) {
      formErrors.push(ERROR.EMPTY_LOCATION)
    }
    this.setState({
      formErrors
    })
    if (!_.isEmpty(formErrors)) {
      return;
    }

    // Build and submit correctly formatted input
    this.props.onPrint(this.buildFinalInput())
  }

  buildFinalInput() {
    return _.extend(
      _.pick(this.state.input, 'title', 'people', 'remark'),
      this.props.data
        ? {}
        : {
            date: this.state.input.date,
            location: {
              id: this.state.input.location
            }
          }
    )
  }

  render() {
    return (
      <React.Fragment>
        {
          this.props.error
            ? <Dismissible
                header={AppUtils.errorToTitle(this.props.error)}
                content={AppUtils.errorToText(this.props.error)}
                color='red'
                onDismiss={this.props.onClearError}
              />
            : null
        }
        {
          !_.isEmpty(this.state.formErrors)
            ? <Dismissible
                content='Veuillez résoudre les problèmes ci-dessous avant de poursuivre.'
                color='orange'
              />
            : null
        }
        <Form
          onSubmit={this.onSubmit}
          loading={this.props.isLoading || this.props.isSaving}
        >
          {
            this.props.data
              ? <StaticInput
                  label='Date'
                  content={AppUtils.displayDate(this.state.input.date)}
                />
              : <DateInput
                  label='Date'
                  value={this.state.input.date}
                  onChange={this.onChangeInputDate}
                  allowBlurEmpty={false}
                  error={
                    _.contains(this.state.formErrors, ERROR.EMPTY_DATE)
                      ? ERROR.EMPTY_DATE
                      : null
                  }
                />
          }
          {
            this.props.data
              ? <StaticInput
                  label='Emplacement'
                  content={
                    <LocationStickers
                      stickers={this.props.data.location.stickers}
                    />
                  }
                />
              : <LocationSelect
                  label='Emplacement'
                  loading={this.props.locationsIsFetching}
                  options={this.props.locationsList}
                  selected={this.state.input.location}
                  onChange={this.onChangeInputLocation}
                  error={
                    _.contains(this.state.formErrors, ERROR.EMPTY_LOCATION)
                      ? ERROR.EMPTY_LOCATION
                      : null
                  }
                />
          }
          <TextInput
            label='Titre'
            placeholder='Hangar Buix'
            value={this.state.input.title}
            onChange={this.onChangeInputTitle}
            error={
              _.contains(this.state.formErrors, ERROR.EMPTY_TITLE)
                ? ERROR.EMPTY_TITLE
                : null
            }
          />
          {
            this.props.data
              ? <StaticInput
                  label='Saisi par'
                  content={this.props.data.user.name}
                />
              : null
          }
          <TextInput
            label='Participants'
            placeholder='Jean et Pierre'
            value={this.state.input.people}
            onChange={this.onChangeInputPeople}
          />
          <TextInput
            label='Remarque'
            placeholder='...'
            value={this.state.input.remark}
            onChange={this.onChangeInputRemark}
            multiline={true}
          />
        </Form>
      </React.Fragment>
    )
  }
}

InventoryForm.propTypes = {
  data: PropTypes.object,
  location: PropTypes.number,
  onSubmit: PropTypes.func.isRequired,
  onPrint: PropTypes.func,
  onClearError: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isSaving: PropTypes.bool.isRequired,
  registerSubmit: PropTypes.func,
  registerPrint: PropTypes.func,
  error: PropTypes.object,
  locationsList: PropTypes.array,
  locationsIsFetching: PropTypes.bool.isRequired,
}

export default InventoryForm



// WEBPACK FOOTER //
// ./src/components/forms/InventoryForm.js